import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useEffect } from "react";
import { Input } from "@/shared/components/Input";
import { CampoDataNascimento } from "@/shared/components/CampoDataNascimento";
import { Button } from "@/shared/components/Button";
import { BotaoVoltar } from "@/shared/components/BotaoVoltar";
import { cpfEhValido } from "@/core/utils/cpf";
import { dataHojeInput } from "@/shared/lib/datas";
import { CampoSexo } from "@/shared/components/CampoSexo";
import { soSexo } from "@/shared/lib/sexo";
import type { Pessoa } from "@/core/services/pessoasService";
import type { PessoaPerfil } from "@/shared/types/database.types";

// Limites espelhando exatamente as colunas do banco (migrations 0001/0016):
// pessoas.nome_completo varchar(100), pessoas.telefone varchar(20), pessoas.email varchar(100).
const LIMITE_NOME = 100;
const LIMITE_TELEFONE = 20;
const LIMITE_EMAIL = 100;

/**
 * Um único schema para os dois modos (criar/editar). Em modo edição, `senha`
 * fica como string vazia e não é validada nem enviada (ver onSubmit) — isso
 * evita ter dois schemas distintos e dois tipos de formulário, simplificando
 * a tipagem do useForm.
 */
const formularioSchema = z.object({
  cpf: z
    .string()
    .min(1, "Informe o CPF.")
    .refine((v) => cpfEhValido(v), "CPF inválido."),
  nomeCompleto: z
    .string()
    .min(3, "Nome muito curto.")
    .max(LIMITE_NOME, `O nome pode ter no máximo ${LIMITE_NOME} caracteres.`),
  telefone: z.string().max(LIMITE_TELEFONE, `Telefone pode ter no máximo ${LIMITE_TELEFONE} caracteres.`).optional(),
  email: z
    .string()
    .email("E-mail inválido.")
    .max(LIMITE_EMAIL, `E-mail pode ter no máximo ${LIMITE_EMAIL} caracteres.`),
  // F2-41 (pedido do usuário, 26/07/2026): obrigatória também na criação E
  // na edição pelo admin — é assim que se corrige o valor temporário que a
  // migration 0026 preencheu em cadastros antigos sem essa data.
  dataNascimento: z
    .string()
    .min(1, "Informe a data de nascimento.")
    .refine((v) => v <= dataHojeInput(), "A data de nascimento não pode ser no futuro."),
  // Sexo (02/11/2026): obrigatório ao CRIAR (conferido no superRefine abaixo). Ao EDITAR, cadastro
  // antigo sem o dado pode ser salvo em branco — não trava a correção de outro campo.
  sexo: z.enum(["masculino", "feminino", ""]),
  perfil: z.enum(["administrador", "membro"]),
  senha: z.string(),
  _ehEdicao: z.boolean().optional(),
}).superRefine((dados, ctx) => {
  if (!dados._ehEdicao && dados.sexo === "") {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Informe o sexo.", path: ["sexo"] });
  }
  if (!dados._ehEdicao && dados.senha.length < 6) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "A senha precisa ter pelo menos 6 caracteres.",
      path: ["senha"],
    });
  }
});

export type FormularioUsuarioValores = z.infer<typeof formularioSchema>;

interface FormularioUsuarioProps {
  pessoaEmEdicao?: Pessoa | null;
  carregando: boolean;
  /** Erro vindo do backend (ex: CPF duplicado), exibido dentro do próprio modal. */
  erro?: string | null;
  /** Avisa o componente pai sempre que o formulário tiver alterações não salvas. */
  onDirtyChange?: (sujo: boolean) => void;
  onSubmitCriar?: (dados: FormularioUsuarioValores & { senha: string }) => void;
  /**
   * Edição NÃO inclui perfil (Skill v3.0): a troca Administrador/Membro é
   * feita pela etiqueta clicável na listagem de Usuários, não aqui.
   */
  onSubmitEditar?: (dados: Omit<FormularioUsuarioValores, "senha" | "perfil" | "_ehEdicao">) => void;
}

export function FormularioUsuario({
  pessoaEmEdicao,
  carregando,
  erro,
  onDirtyChange,
  onSubmitCriar,
  onSubmitEditar,
}: FormularioUsuarioProps) {
  const ehEdicao = !!pessoaEmEdicao;

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isDirty },
  } = useForm<FormularioUsuarioValores>({
    resolver: zodResolver(formularioSchema),
    defaultValues: { perfil: "membro", dataNascimento: "", sexo: "", senha: "", _ehEdicao: false },
  });

  // Guardados em variável (em vez de espalhar {...register(...)} direto no
  // JSX) para poder encadear o onChange customizado de limpeza de dígitos
  // SEM sobrescrever o onChange do React Hook Form — se sobrescrever, o
  // formulário nunca fica sabendo que o campo mudou (bug real encontrado:
  // "sair sem salvar" não disparava para Telefone).
  const registroCpf = register("cpf");
  const registroTelefone = register("telefone");

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const nomeAtual = watch("nomeCompleto") ?? "";
  const telefoneAtual = watch("telefone") ?? "";
  const emailAtual = watch("email") ?? "";

  useEffect(() => {
    if (pessoaEmEdicao) {
      // cpf/email são anuláveis em `pessoas` (visitante pode não ter) —
      // usuários com login sempre têm, mas o tipo exige o fallback.
      reset({
        cpf: pessoaEmEdicao.cpf ?? "",
        nomeCompleto: pessoaEmEdicao.nome_completo,
        telefone: pessoaEmEdicao.telefone ?? "",
        email: pessoaEmEdicao.email ?? "",
        dataNascimento: pessoaEmEdicao.data_nascimento,
        sexo: soSexo(pessoaEmEdicao.sexo),
        perfil: pessoaEmEdicao.perfil as PessoaPerfil,
        senha: "",
        _ehEdicao: true,
      });
    }
  }, [pessoaEmEdicao, reset]);

  function onSubmit(dados: FormularioUsuarioValores) {
    if (ehEdicao && onSubmitEditar) {
      const { senha: _senha, _ehEdicao: _flag, perfil: _perfil, ...resto } = dados;
      onSubmitEditar(resto);
    } else if (!ehEdicao && onSubmitCriar) {
      onSubmitCriar({ ...dados, senha: dados.senha });
    }
  }

  function contador(valor: string, limite: number) {
    return (
      <p className={`text-right text-xs ${valor.length >= limite ? "font-medium text-warning" : "text-muted"}`}>
        {valor.length}/{limite}
      </p>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4" noValidate>
      <div className="flex flex-col gap-1">
        <Input
          label="CPF"
          placeholder="000.000.000-00"
          disabled={ehEdicao}
          inputMode="numeric"
          erro={errors.cpf?.message}
          {...registroCpf}
          onChange={(e) => {
            e.target.value = e.target.value.replace(/\D/g, "").slice(0, 11);
            registroCpf.onChange(e);
          }}
        />
        {ehEdicao && <p className="text-xs text-muted">CPF não pode ser alterado depois do cadastro.</p>}
      </div>
      <div className="flex flex-col gap-1">
        <Input
          label="Nome completo"
          maxLength={LIMITE_NOME}
          erro={errors.nomeCompleto?.message}
          {...register("nomeCompleto")}
        />
        {contador(nomeAtual, LIMITE_NOME)}
      </div>
      <div className="flex flex-col gap-1">
        <Input
          label="Telefone"
          placeholder="(00) 00000-0000"
          inputMode="tel"
          maxLength={LIMITE_TELEFONE}
          erro={errors.telefone?.message}
          {...registroTelefone}
          onChange={(e) => {
            e.target.value = e.target.value.replace(/\D/g, "").slice(0, 11);
            registroTelefone.onChange(e);
          }}
        />
        {contador(telefoneAtual, LIMITE_TELEFONE)}
      </div>
      <div className="flex flex-col gap-1">
        <Input
          label="E-mail"
          type="email"
          maxLength={LIMITE_EMAIL}
          erro={errors.email?.message}
          {...register("email")}
        />
        {contador(emailAtual, LIMITE_EMAIL)}
      </div>
      <div className="flex flex-col gap-1">
        <Controller
          name="dataNascimento"
          control={control}
          render={({ field }) => (
            <CampoDataNascimento
              label="Data de nascimento"
              name={field.name}
              ref={field.ref}
              value={field.value ?? ""}
              onChange={field.onChange}
              onBlur={field.onBlur}
              erro={errors.dataNascimento?.message}
            />
          )}
        />
        {ehEdicao && (
          <p className="text-xs text-muted">
            Cadastros antigos foram preenchidos com uma data provisória — corrija aqui se for o caso.
          </p>
        )}
      </div>
      <Controller
        name="sexo"
        control={control}
        render={({ field }) => (
          <CampoSexo
            valor={field.value ?? ""}
            onChange={field.onChange}
            erro={errors.sexo?.message}
            ajuda={
              ehEdicao && !pessoaEmEdicao?.sexo
                ? "Cadastro antigo, sem o sexo informado. Escolha uma opção para completar."
                : undefined
            }
          />
        )}
      />
      {/* Perfil só na CRIAÇÃO. Na edição, a troca é pela etiqueta na listagem (Skill v3.0). */}
      {!ehEdicao && (
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-text">Perfil</label>
          <select
            className="rounded-md border border-transparent bg-surface-2 px-4 py-2.5 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary"
            {...register("perfil")}
          >
            {/* DA-002 Nível 1: perfil da PLATAFORMA. Gestão de módulo se define no Ministério. */}
            <option value="membro">Membro</option>
            <option value="administrador">Administrador da igreja</option>
          </select>
        </div>
      )}
      {!ehEdicao && (
        <div className="flex flex-col gap-1">
          <Input label="Senha" type="password" erro={errors.senha?.message} {...register("senha")} />
          <p className="text-xs text-muted">Mínimo de 6 caracteres.</p>
        </div>
      )}

      {/* Erro do backend (ex: CPF duplicado) — exibido DENTRO do modal, não atrás dele. */}
      {erro && (
        <p role="alert" className="rounded-md bg-danger/10 px-3 py-2 text-sm text-danger">
          {erro}
        </p>
      )}

      <Button type="submit" carregando={carregando} className="mt-2">
        {ehEdicao ? "Salvar alterações" : "Criar usuário"}
      </Button>
      <BotaoVoltar disabled={carregando} className="-mt-2" />
    </form>
  );
}
